<div class="kyb-entity-summary-component">
  <div class="row separator">
    <div class="col-md-12" fxLayout="row" fxLayoutAlign="space-between center">
      <div class="mat-h3">Business Entities</div>
    </div>
  </div>

  @if (!loaded) {
  <mat-card appearance="outlined">
    <mat-card-content>
      <div class="section-loading">
        <mat-spinner [diameter]="26" [strokeWidth]="2"></mat-spinner>
        <span>Loading business entities...</span>
      </div>
    </mat-card-content>
  </mat-card>
  }

  @if (loaded && reconciliationAttentionCount()) {
  <message-box type="warn">
    {{ reconciliationAttentionCount() }} business
    {{ reconciliationAttentionCount() === 1 ? 'entity requires' : 'entities require' }}
    KYB people reconciliation.
  </message-box>
  }

  @if (loaded && keyPeopleRequirementIssues(); as issues) {
  @if (issues.length) {
  <message-box type="warn" title="These entities do not meet key people requirements">
    <ul class="requirement-issues">
      @for (issue of issues; track issue.entityId) {
      <li>
        <strong>{{ issue.entityName }}</strong>
        @for (error of issue.errors; track error) {
        <div>{{ error }}</div>
        }
      </li>
      }
    </ul>
  </message-box>
  }
  }

  @if (loaded && !rows.length) {
  <div class="row">
    <div class="col-lg-9 col-md-12">
      <message-box type="info">
        <p>No business entities on this application</p>
      </message-box>
    </div>
  </div>
  }

  @if (loaded && rows.length) {
  <mat-card appearance="outlined">
    <mat-card-content class="px-0">
      <div class="row">
        <div class="col-12 entity-details-section">
          <table mat-table class="entities-table" [dataSource]="dataSource" multiTemplateDataRows>
            <ng-container matColumnDef="dir">
              <th mat-header-cell *matHeaderCellDef class="column-width-0"></th>
              <td mat-cell *matCellDef="let element" class="cell cell-padding">
                @if (isTableRowExpanded(element)) {
                <span class="mdi mdi-chevron-up"></span>
                }
                @if (!isTableRowExpanded(element)) {
                <span class="mdi mdi-chevron-down"></span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef class="column-width-3">Name</th>
              <td mat-cell *matCellDef="let element" class="cell name-cell">
                <div class="ellipsis entity-name" [class.staged-removal]="isRemovalStaged(element.entity)"
                  matTooltip="{{element.entity.entityName}}">
                  {{ element.entity.entityName }}</div>
                @if (rowRequiresReconciliation(element.entity)) {
                <span class="reconciliation-chip">KYB reconciliation required</span>
                }
                @if (rowHasKeyPeopleRuleErrors(element.entity)) {
                <span class="requirements-chip">Requirements not met</span>
                }
                @if (isRemovalStaged(element.entity)) {
                <span class="pending-removal-chip">Pending removal</span>
                }
                <!-- Related parties sit as muted caption lines under the entity, one per party;
                     the individuals table above already lists each person in full. -->
                @for (party of element.parties; track party) {
                <div class="entity-parties ellipsis" matTooltip="{{party}}">{{ party }}</div>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="entityType">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Entity Type</th>
              <td mat-cell *matCellDef="let element" class="cell">{{ typeLabel(element.entity.entityType) }}</td>
            </ng-container>

            <ng-container matColumnDef="entityRole">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">Entity Role</th>
              <td mat-cell *matCellDef="let element" class="cell">{{ roleLabel(element.entity) }}</td>
            </ng-container>

            <ng-container matColumnDef="riskScore">
              <th mat-header-cell *matHeaderCellDef class="column-width-0">Risk Score</th>
              <td mat-cell *matCellDef="let element" class="cell">
                @if (riskScoreLabel(element.entity.riskScore); as score) {
                {{score}}
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="riskRating">
              <th mat-header-cell *matHeaderCellDef class="column-width-0">Risk Rating</th>
              <td mat-cell *matCellDef="let element" class="cell">
                @if (riskLevelLabel(element.entity.riskLevel); as rating) {
                <span class="risk-rating {{riskLevelClass(element.entity.riskLevel)}}">{{rating}}</span>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="kybStatus">
              <th mat-header-cell *matHeaderCellDef class="column-width-1">KYB Result</th>
              <td mat-cell *matCellDef="let element" class="cell">
                <kyb-status-chip [status]="statusDisplay(element.entity)"></kyb-status-chip>
              </td>
            </ng-container>

            <ng-container matColumnDef="action">
              <th mat-header-cell *matHeaderCellDef class="column-width-0"></th>
              <td mat-cell *matCellDef="let element" class="cell" (click)="$event.stopPropagation()">
                @if (isRemovalStaged(element.entity)) {
                <span class="mdi mdi-undo cursor-pointer" matTooltip="Undo removal"
                  (click)="onUndoRemoveParty(element.entity)"></span>
                } @else if (showEntityActions(element.entity)) {
                <span class="mdi mdi-dots-vertical cursor-pointer" matTooltip="More actions"
                  [matMenuTriggerFor]="entityActionsMenu"></span>
                <mat-menu #entityActionsMenu="matMenu">
                  @if (canChangeParties() && addablePartyKind(element.entity)) {
                  <button mat-menu-item (click)="onAddParty(element.entity)">
                    <span class="mdi mdi-domain-plus"></span> {{ addPartyLabel(element.entity) }} entity
                  </button>
                  <button mat-menu-item (click)="onAddIndividualParty(element.entity)">
                    <span class="mdi mdi-account-plus"></span> {{ addPartyLabel(element.entity) }} individual
                  </button>
                  }
                  @if (isTrust(element.entity)) {
                  <button mat-menu-item (click)="onUploadTrustDeed(element.entity)">
                    <span class="mdi mdi-file-upload-outline"></span> {{ trustDeedButtonLabel(element.entity) }}
                  </button>
                  }
                  @if (canEdit) {
                    @if (isEditing(element)) {
                    <button mat-menu-item (click)="cancelEdit(element)">
                      <span class="mdi mdi-close"></span> Cancel edit
                    </button>
                    } @else {
                    <button mat-menu-item (click)="startEdit(element)">
                      <span class="mdi mdi-pencil-outline"></span> Edit details
                    </button>
                    }
                  }
                  @if (canReevaluate(element.entity)) {
                  <button mat-menu-item (click)="onReevaluateRisk(element.entity)"
                    [disabled]="isReevaluationStarting(element.entity)"
                    [matTooltip]="reevaluateRiskTooltip">
                    <span class="mdi mdi-refresh"></span> Re-evaluate risk
                  </button>
                  }
                  @if (canChangeParties() && !element.entity.isPrimary) {
                  <button mat-menu-item (click)="onRemoveParty(element.entity)">
                    <span class="mdi mdi-delete-outline"></span> Remove entity
                  </button>
                  }
                </mat-menu>
                }
              </td>
            </ng-container>

            <ng-container matColumnDef="expandedDetail">
              <td mat-cell *matCellDef="let element" [attr.colspan]="columnsToDisplay.length">
                <div class="element-detail" [@detailExpand]="isTableRowExpanded(element) ? 'expanded' : 'collapsed'">
                  @if (isTableRowExpanded(element)) {
                  @if (isTrust(element.entity) && !isRemovalStaged(element.entity)) {
                  <div class="detail-actions" fxLayout="row" fxLayoutAlign="end center" fxLayoutGap="8px">
                    <button mat-stroked-button (click)="onUploadTrustDeed(element.entity)"
                      matTooltip="Upload the trust deed to extract the trust's parties automatically">
                      <span class="mdi mdi-file-upload-outline"></span> {{ trustDeedButtonLabel(element.entity) }}
                    </button>
                  </div>
                  }

                  <!-- A corporate trustee/partner has its own KYB run, so it gets its own result
                       block here rather than a row of its own in the table. The list leads the
                       panel so the related entities are visible the moment the row expands. The
                       header bar is always visible - the child has no table row to identify it -
                       and the block stays collapsed until the reviewer asks for it, so a trustee's
                       detail does not double the length of the parent's panel.

                       The template recurses because the chain does: a partnership trustee can hold
                       a company partner of its own, which is a party to the application with a KYB
                       run like any other and must not disappear for sitting a level further down. -->
                  <ng-template #individualPartyBlocks let-parties>
                    @for (party of parties; track party.individualId) {
                    <div class="child-block individual-party-block">
                      <div class="child-header">
                        <span class="mdi mdi-account-outline party-kind-icon"></span>
                        <span class="child-title-group">
                          <span class="child-title"
                            [class.staged-removal]="isIndividualRemovalStaged(party.individualId)">
                            {{ individualPartyName(party) }}
                          </span>
                          <span class="child-subtitle">Individual · {{ individualPartyRoleLabel(party) }}</span>
                        </span>
                        @if (party.dateOfBirth) {
                        <span class="individual-dob">DOB {{ party.dateOfBirth | date:'dd/MM/yyyy' }}</span>
                        }
                        @if (isIndividualRemovalStaged(party.individualId)) {
                        <span class="pending-removal-chip">Pending removal</span>
                        }
                        @if (canChangeParties()) {
                        @if (isIndividualRemovalStaged(party.individualId)) {
                        <span class="mdi mdi-undo child-remove" matTooltip="Undo removal"
                          (click)="onUndoRemoveIndividualParty(party)"></span>
                        } @else {
                        <span class="mdi mdi-dots-vertical child-remove" matTooltip="More actions"
                          [matMenuTriggerFor]="individualActionsMenu"></span>
                        }
                        }
                      </div>
                      <mat-menu #individualActionsMenu="matMenu">
                        <button mat-menu-item (click)="onRemoveIndividualParty(party)">
                          <span class="mdi mdi-delete-outline"></span>
                          Remove {{ party.isTrustee ? 'trustee' : 'partner' }}
                        </button>
                      </mat-menu>
                    </div>
                    }
                  </ng-template>

                  <ng-template #childBlocks let-children let-editing="editing">
                    @for (child of children; track child.commercialEntityId) {
                    <div class="child-block" [class.entity-reconciliation-block]="requiresReconciliation(child)">
                      <button type="button" class="child-header" (click)="toggleChild(child)">
                        <span class="mdi child-chevron"
                          [ngClass]="isChildExpanded(child) ? 'mdi-chevron-down' : 'mdi-chevron-right'"></span>
                        <span class="child-title-group">
                          <span class="child-title" [class.staged-removal]="isRemovalStaged(child)">{{ child.entityName }}</span>
                          <span class="child-subtitle">{{ typeLabel(child.entityType) }} · {{ roleLabel(child) }}</span>
                        </span>
                        @if (requiresReconciliation(child)) {
                        <span class="reconciliation-chip">KYB reconciliation required</span>
                        }
                        @if (ruleErrorsFor(child).length) {
                        <span class="requirements-chip">Requirements not met</span>
                        }
                        @if (isRemovalStaged(child)) {
                        <span class="pending-removal-chip">Pending removal</span>
                        }
                        @if (childNeedsAttention(child)) {
                        <span class="mdi mdi-alert-circle-outline child-attention"
                          matTooltip="This entity has screening results or unmet rules that need attention"></span>
                        }
                        <kyb-risk-badge [score]="child.riskScore" [level]="child.riskLevel"></kyb-risk-badge>
                        <kyb-status-chip [status]="statusDisplay(child)"></kyb-status-chip>
                        @if (showNestedEntityActions(child)) {
                        @if (canChangeParties() && isRemovalStaged(child)) {
                        <span class="mdi mdi-undo child-remove" matTooltip="Undo removal"
                          (click)="onUndoRemoveParty(child); $event.stopPropagation()"></span>
                        } @else {
                        <span class="mdi mdi-dots-vertical child-remove" matTooltip="More actions"
                          [matMenuTriggerFor]="childActionsMenu" (click)="$event.stopPropagation()"></span>
                        }
                        }
                      </button>
                      <mat-menu #childActionsMenu="matMenu">
                        @if (canChangeParties()) {
                        @if (addablePartyKind(child)) {
                        <button mat-menu-item (click)="onAddParty(child)">
                          <span class="mdi mdi-domain-plus"></span> {{ addPartyLabel(child) }} entity
                        </button>
                        <button mat-menu-item (click)="onAddIndividualParty(child)">
                          <span class="mdi mdi-account-plus"></span> {{ addPartyLabel(child) }} individual
                        </button>
                        }
                        <button mat-menu-item (click)="onRemoveParty(child)">
                          <span class="mdi mdi-delete-outline"></span> Remove entity
                        </button>
                        }
                        @if (canReevaluate(child)) {
                        <button mat-menu-item (click)="onReevaluateRisk(child)"
                          [disabled]="isReevaluationStarting(child)"
                          [matTooltip]="reevaluateRiskTooltip">
                          <span class="mdi mdi-refresh"></span> Re-evaluate risk
                        </button>
                        }
                      </mat-menu>
                      @if (isChildExpanded(child)) {
                      @if (isTrust(child) && !isRemovalStaged(child)) {
                      <div class="child-actions" fxLayout="row" fxLayoutGap="8px">
                        <button mat-stroked-button (click)="onUploadTrustDeed(child)"
                          matTooltip="Upload the trust deed to extract the trust's parties automatically">
                          <span class="mdi mdi-file-upload-outline"></span> {{ trustDeedButtonLabel(child) }}
                        </button>
                      </div>
                      }
                      @if (naturalPersonParties(child.individualParties); as individualParties) {
                      @if (child.children.length || individualParties.length) {
                      <div class="child-children">
                        <ng-container
                          *ngTemplateOutlet="childBlocks; context: { $implicit: child.children, editing: editing }"></ng-container>
                        <ng-container
                          *ngTemplateOutlet="individualPartyBlocks; context: { $implicit: individualParties }"></ng-container>
                      </div>
                      }
                      }
                      <kyb-result-block [entity]="child" [nested]="true"
                        [editable]="editing"
                        [canRunWorkflow]="canRunWorkflow"
                        [workflowStarting]="isWorkflowStarting(child)"
                        [ruleErrors]="ruleErrorsFor(child)"
                        [ruleWarnings]="ruleWarningsFor(child)"
                        [resolutions]="stagedResolutions(child)"
                        [stagedKeyPeople]="stagedKeyPeople()"
                        (keyPersonChange)="onKeyPersonChange($event)"
                        (keyPersonDetailsRequired)="keyPersonDetailsRequired.emit($event)"
                        (personResolutionUndo)="personResolutionUndo.emit($event)"
                        (runWorkflow)="onRunKybWorkflow($event)"
                        (downloadReport)="onDownloadKybReport($event)"></kyb-result-block>

                      <!-- A deed uploaded against a corporate trustee that is itself a trust used to
                           produce a result nothing rendered, because this block existed only on the
                           top-level row. -->
                      <ng-container
                        *ngTemplateOutlet="trustAnalysisBlocks; context: { $implicit: child }"></ng-container>
                      }
                    </div>
                    }
                  </ng-template>

                  <!-- One block per deed condition, showing whichever version the reviewer has
                       switched to. -->
                  <ng-template #trustAnalysisBlocks let-trustEntity>
                    @for (history of trustAnalysisHistoriesFor(trustEntity); track trackTrustHistory($index, history)) {
                    <trust-analysis-block [analysis]="selectedTrustAnalysis(history)"
                      [identity]="trustAnalysisSubtitle(selectedTrustAnalysis(history))"
                      [versions]="history.versions"
                      (selectVersion)="onSelectTrustVersion(history, $event)"
                      (retry)="onRetryTrustAnalysis($event)"
                      (downloadDeed)="onDownloadTrustDeed($event)"></trust-analysis-block>
                    }
                  </ng-template>

                  <ng-container
                    *ngTemplateOutlet="childBlocks; context: { $implicit: element.entity.children, editing: isEditing(element) }"></ng-container>
                  <ng-container
                    *ngTemplateOutlet="individualPartyBlocks; context: { $implicit: naturalPersonParties(element.entity.individualParties) }"></ng-container>

                  <ng-container
                    *ngTemplateOutlet="trustAnalysisBlocks; context: { $implicit: element.entity }"></ng-container>

                  <kyb-result-block [entity]="element.entity"
                    [keyCompanyDetailsForm]="entityForm(element)"
                    [placesSearchResultFn]="placesSearchResultFn"
                    [placesDetailsFn]="placesDetailsFn"
                    [editable]="isEditing(element)"
                    [canRunWorkflow]="canRunWorkflow"
                    [workflowStarting]="isWorkflowStarting(element.entity)"
                    [ruleErrors]="ruleErrorsFor(element.entity)"
                    [ruleWarnings]="ruleWarningsFor(element.entity)"
                    [resolutions]="stagedResolutions(element.entity)"
                    [stagedKeyPeople]="stagedKeyPeople()"
                    (keyPersonChange)="onKeyPersonChange($event)"
                    (keyPersonDetailsRequired)="keyPersonDetailsRequired.emit($event)"
                    (personResolutionUndo)="personResolutionUndo.emit($event)"
                    (runWorkflow)="onRunKybWorkflow($event)"
                    (downloadReport)="onDownloadKybReport($event)"></kyb-result-block>
                  }
                </div>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="columnsToDisplay"></tr>
            <tr mat-row *matRowDef="let element; columns: columnsToDisplay" class="cursor-pointer"
              [class.expanded-row]="isTableRowExpanded(element)"
              [class.staged-removal-row]="isRemovalStaged(element.entity)"
              [class.entity-reconciliation-row]="rowRequiresReconciliation(element.entity)"
              (click)="expandTableRow(element)"></tr>
            <tr class="detail-row" [ngClass]="{'detail-row-expanded': isTableRowExpanded(element)}"
              [ngStyle]="{'display': isTableRowExpanded(element) ? null : 'none'}" mat-row
              *matRowDef="let element; columns: ['expandedDetail']"></tr>
          </table>
        </div>
      </div>
    </mat-card-content>
  </mat-card>
  }
</div>
